/* eslint-disable i18next/no-literal-string -- a code-only module: every literal is a state, hold, decision, error or wire code; it renders nothing */
// C4 Durable op queue for the web (spec C4.1-C4.5): every write made while
// it cannot (or might not) reach the server is an immutable record in the
// owner's fenced `queue` store, drained exactly once by the scheduler, under
// the owner it was made by.
//
//  - Record: everything above `state` is immutable after creation. `request`
//    holds ONLY the bytes the server receives (base64); temp ids and the like
//    live in `meta` / `local_refs` and are never sent.
//  - FIFO per scope. A scope head that is ambiguous or held blocks what is
//    behind it; done and failed_permanent do not.
//  - in_flight is persisted BEFORE the request leaves; in_flight found at
//    launch is ambiguous; an ambiguous op_key record is settled by re-sending
//    the SAME bytes (the server ledger answers the stored response) before
//    anything newer in its scope.
//  - Before dispatch and after every await: owner, incarnation, session
//    subject, C3 gate and scope still match, else a hold. Never retarget.
//  - A record 25 days old or more is held needs_review; Send is allowed
//    only below 29 days (the ledger keeps receipts 30).
//
// Collection adds (C4.4) are built here; deck edits (C4.6) in deckOutbox.ts.
import { rfc3339 } from './time'
import {
  ADD_SOURCE_MANUAL, CHUNK_ITEMS, CONTENT_TYPE_JSON, DAY_MS, DECISION, HOLD, METHOD, MODE, OP_KIND, OP_STATE, OUTCOME,
  REVIEW_AFTER_DAYS, SCHEMA_VERSION, SEND_ALLOWED_BELOW_DAYS, SERVICE, STORE,
  type HoldReason, type IdentityMode, type OpKind, type OpOutcome, type OpState,
} from './offline.codes'
import { opOutcome, type HttpExchange } from './httpOutcome'
import { backoffDelayMs } from './schedulerCore'
import { fencedWrite, ownerGetAll, type FencedResult, type WriterCtx } from './offlineStore'

// ---- Record -------------------------------------------------------------------------------

export interface OpRecord {
  schema_version: number
  op_id: string
  kind: OpKind
  owner_key: string | null
  owner_incarnation: number | null
  scope: { type: 'collection' | 'deck' | 'scan_inbox'; id: string }
  destination: { service: 'api' | 'supabase'; method: string; path: string; headers?: Record<string, string> }
  request: { content_type: string; body: string }
  op_key: string | null
  group: { base_op_key: string; chunk_index: number; chunk_count: number } | null
  meta: Record<string, unknown>
  created_at: string
  // ---- mutable from here ----
  state: OpState
  hold: { reason: HoldReason; since: string } | null
  attempts: number
  last_attempt_at: string | null
  next_attempt_at: string | null
  last_error: { kind: string; status?: number; detail?: string | null; code?: string } | null
  receipt: unknown
  local_refs: { index: number; temp_id: string }[]
  /** The state an in_flight attempt was dispatched from (pending | ambiguous). */
  dispatched_from?: OpState | null
  /** Consecutive transient / ambiguous outcomes (C1b backoff n). */
  backoff_n?: number
  /** The user chose Send from review (allowed below 29 days). */
  review_send?: boolean
}

export const IMMUTABLE_FIELDS = [
  'schema_version', 'op_id', 'kind', 'owner_key', 'owner_incarnation', 'scope', 'destination', 'request', 'op_key', 'group', 'meta', 'created_at',
] as const

// ---- Bytes ----------------------------------------------------------------------------------

export function utf8ToBase64(s: string): string {
  const bytes = new TextEncoder().encode(s)
  let bin = ''
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return btoa(bin)
}

export function base64ToUtf8(b64: string): string {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new TextDecoder().decode(bytes)
}

/** The exact request bytes as text (never re-serialized). */
export function requestText(r: OpRecord): string {
  return base64ToUtf8(r.request.body)
}

// ---- Collection adds (C4.4) ------------------------------------------------------------------

export interface AddItemWire {
  [k: string]: unknown
}

const URLSAFE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

/** 32 urlsafe random characters. */
export function newBaseOpKey(rand: (n: number) => Uint8Array = defaultRandom): string {
  const b = rand(32)
  let out = ''
  for (let i = 0; i < 32; i++) out += URLSAFE[b[i] & 63]
  return out
}

function defaultRandom(n: number): Uint8Array {
  const a = new Uint8Array(n)
  crypto.getRandomValues(a)
  return a
}

export function newOpId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
}

export interface BuildAddArgs {
  ownerKey: string
  incarnation: number
  collectionId: string
  items: AddItemWire[]
  source?: string
  now: Date
  base?: string
  newId?: () => string
  tempId?: (i: number) => string
}

/**
 * Freeze a collection add: chunked ONCE, in input order, into chunks of at
 * most 250 items; one record per chunk; op_key `<base>-c<i>`. The body keys
 * are items, op_key, source (the per-copy add's own shape).
 */
export function buildCollectionAddRecords(a: BuildAddArgs): OpRecord[] {
  const base = a.base ?? newBaseOpKey()
  const chunks: AddItemWire[][] = []
  for (let i = 0; i < a.items.length; i += CHUNK_ITEMS) chunks.push(a.items.slice(i, i + CHUNK_ITEMS))
  const path = `/collections/${encodeURIComponent(a.collectionId)}/copies`
  let global = 0
  return chunks.map((items, ci) => {
    const opKey = `${base}-c${ci}`
    const body = JSON.stringify({ items, op_key: opKey, source: a.source ?? ADD_SOURCE_MANUAL })
    const refs = items.map((_, index) => ({ index, temp_id: a.tempId ? a.tempId(global + index) : `tmp-${base.slice(0, 8)}-${global + index}` }))
    global += items.length
    return {
      schema_version: SCHEMA_VERSION,
      op_id: a.newId ? a.newId() : newOpId(),
      kind: OP_KIND.collectionAdd,
      owner_key: a.ownerKey,
      owner_incarnation: a.incarnation,
      scope: { type: 'collection', id: a.collectionId },
      destination: { service: SERVICE.api, method: METHOD.post, path },
      request: { content_type: CONTENT_TYPE_JSON, body: utf8ToBase64(body) },
      op_key: opKey,
      group: { base_op_key: base, chunk_index: ci, chunk_count: chunks.length },
      meta: {},
      created_at: rfc3339(a.now),
      state: OP_STATE.pending,
      hold: null,
      attempts: 0,
      last_attempt_at: null,
      next_attempt_at: null,
      last_error: null,
      receipt: null,
      local_refs: refs,
    }
  })
}

/** The items a collection_add record carries (from its frozen bytes). */
export function itemsOf(r: OpRecord): AddItemWire[] {
  try {
    const body = JSON.parse(requestText(r)) as { items?: unknown }
    return Array.isArray(body.items) ? (body.items as AddItemWire[]) : []
  } catch {
    return []
  }
}

export interface AddReceipt {
  created: { index: number; copy_id: number }[]
  merged: { copy_id: number; qty: number }[]
  merged_items?: { index: number; copy_id: number }[]
  errors: { index: number; code: string }[]
}

/** Done check for a collection add: a 2xx whose body has created, merged and errors arrays. */
export function isAddReceipt(body: unknown): body is AddReceipt {
  if (!body || typeof body !== 'object') return false
  const o = body as Record<string, unknown>
  return Array.isArray(o.created) && Array.isArray(o.merged) && Array.isArray(o.errors)
}

export interface Reconciliation {
  resolved: Record<string, number>
  failed: Record<string, string>
  /** Temp rows dropped for an authoritative re-read (a pre-A4 merge without indexes). */
  dropped: string[]
  /** Refetch the A1 snapshot before showing the collection. */
  needsRefetch: boolean
}

/**
 * Every input index ends mapped to a copy id (created or merged_items), an
 * error code, or an authoritative refetch. A merged line without A4's index
 * is never guessed.
 */
export function reconcileAddReceipt(r: OpRecord, receipt: AddReceipt): Reconciliation {
  const byIndex = new Map(r.local_refs.map(l => [l.index, l.temp_id]))
  const out: Reconciliation = { resolved: {}, failed: {}, dropped: [], needsRefetch: false }
  const seen = new Set<number>()
  for (const c of receipt.created) {
    const t = byIndex.get(c.index)
    if (t) { out.resolved[t] = c.copy_id; seen.add(c.index) }
  }
  for (const m of receipt.merged_items ?? []) {
    const t = byIndex.get(m.index)
    if (t) { out.resolved[t] = m.copy_id; seen.add(m.index) }
  }
  for (const e of receipt.errors) {
    const t = byIndex.get(e.index)
    if (t) { out.failed[t] = e.code; seen.add(e.index) }
  }
  for (const [index, t] of byIndex) {
    if (!seen.has(index)) {
      out.dropped.push(t)
      out.needsRefetch = true
    }
  }
  return out
}

// ---- Decisions (pure) -----------------------------------------------------------------------

export interface DispatchCurrent {
  offlineOwner: string | null
  ownerIncarnation: number
  sessionSubject: string | null
  online: boolean
  mode: IdentityMode
  /** The C3 write gate (mayWrite) for the captured context. */
  gateOpen: boolean
  /** The owner's current collection ids (collection scopes). */
  collectionIds?: string[]
  /** The owner's current deck ids (deck scopes); undefined: not checked. */
  deckIds?: string[]
}

export type Decision =
  | { decision: 'dispatch' | 'replay' | 'read_row' | 'wait' | 'skip' | 'not_due' }
  | { decision: 'hold'; reason: HoldReason }

export function decisionCode(d: Decision): string {
  return d.decision === 'hold' ? `hold:${d.reason}` : d.decision
}

export function ageDays(r: OpRecord, now: number): number {
  return Math.floor((now - Date.parse(r.created_at)) / DAY_MS)
}

/** Holds re-evaluated on every run (the condition may have passed). */
const ENVIRONMENT_HOLDS: ReadonlySet<string> = new Set([
  HOLD.awaitingSession, HOLD.ownerMismatch, HOLD.incarnationMismatch, HOLD.scopeMismatch,
  // Web holds needs_review only for age, which the check above re-derives.
  HOLD.needsReview,
])

/** The head's decision. Never answers dispatch for a record that is not this owner's now. */
export function decideHead(r: OpRecord, cur: DispatchCurrent, now: number): Decision {
  if (r.schema_version !== SCHEMA_VERSION || !r.request || !r.destination || !r.scope) return { decision: 'hold', reason: HOLD.unreadable }
  if (r.state === OP_STATE.done || r.state === OP_STATE.failedPermanent) return { decision: 'skip' }
  const ownerless = r.owner_key === null
  if (!ownerless) {
    if (r.owner_key !== cur.offlineOwner) return { decision: 'hold', reason: HOLD.ownerMismatch }
    if (r.owner_incarnation !== cur.ownerIncarnation) return { decision: 'hold', reason: HOLD.incarnationMismatch }
    if (cur.mode !== MODE.active) return { decision: 'hold', reason: HOLD.awaitingSession }
    if (!cur.sessionSubject || `acct:${cur.sessionSubject.toLowerCase()}` !== r.owner_key) return { decision: 'hold', reason: HOLD.ownerMismatch }
    if (!cur.gateOpen) return { decision: 'hold', reason: HOLD.awaitingSession }
    if (r.scope.type === 'collection' && cur.collectionIds && !cur.collectionIds.includes(r.scope.id)) return { decision: 'hold', reason: HOLD.scopeMismatch }
    if (r.scope.type === 'deck' && cur.deckIds && !cur.deckIds.includes(r.scope.id) && r.state !== OP_STATE.ambiguous) {
      return { decision: 'hold', reason: HOLD.scopeMismatch }
    }
  }
  const age = ageDays(r, now)
  if (age >= REVIEW_AFTER_DAYS && !(r.review_send && age < SEND_ALLOWED_BELOW_DAYS)) return { decision: 'hold', reason: HOLD.needsReview }
  if (r.hold && !ENVIRONMENT_HOLDS.has(r.hold.reason)) return { decision: 'hold', reason: r.hold.reason }
  // Not online: nothing is sent (finding 9); it waits for `recovered`.
  if (!cur.online) return { decision: 'not_due' }
  if (r.next_attempt_at && Date.parse(r.next_attempt_at) > now) return { decision: 'not_due' }
  if (r.state === OP_STATE.ambiguous || r.state === OP_STATE.inFlight) return { decision: r.kind === OP_KIND.deckEdit ? 'read_row' : 'replay' }
  return { decision: 'dispatch' }
}

/** FIFO per scope: the head decides, everything behind an unsettled head waits. */
export function orderQueue(records: OpRecord[]): Map<string, OpRecord[]> {
  const byScope = new Map<string, OpRecord[]>()
  const sorted = [...records].sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1
    : (a.group?.base_op_key === b.group?.base_op_key ? (a.group?.chunk_index ?? 0) - (b.group?.chunk_index ?? 0) : a.op_id < b.op_id ? -1 : 1)))
  for (const r of sorted) {
    const k = `${r.scope?.type ?? '?'}:${r.scope?.id ?? '?'}`
    byScope.set(k, [...(byScope.get(k) ?? []), r])
  }
  return byScope
}

export function decideQueue(records: OpRecord[], cur: DispatchCurrent, now: number): Map<string, Decision> {
  const out = new Map<string, Decision>()
  for (const list of orderQueue(records).values()) {
    let blocked = false
    for (const r of list) {
      if (blocked) {
        out.set(r.op_id, r.state === OP_STATE.done || r.state === OP_STATE.failedPermanent ? { decision: 'skip' } : { decision: 'wait' })
        continue
      }
      const d = decideHead(r, cur, now)
      out.set(r.op_id, d)
      if (d.decision !== 'skip') blocked = true
    }
  }
  return out
}

// ---- Outcomes (pure) ----------------------------------------------------------------------------

/** A record about to be sent: in_flight, persisted BEFORE the request leaves. */
export function markInFlight(r: OpRecord, now: Date): OpRecord {
  return {
    ...r,
    state: OP_STATE.inFlight,
    dispatched_from: r.state === OP_STATE.ambiguous ? OP_STATE.ambiguous : OP_STATE.pending,
    hold: null,
    attempts: r.attempts + 1,
    last_attempt_at: rfc3339(now),
  }
}

/** in_flight found at launch is ambiguous, never pending. */
export function recoverLaunchState(r: OpRecord): OpRecord {
  return r.state === OP_STATE.inFlight ? { ...r, state: OP_STATE.ambiguous } : r
}

export interface Settled {
  record: OpRecord
  outcome: OpOutcome
}

function holdOn(r: OpRecord, reason: HoldReason, now: Date): OpRecord['hold'] {
  return r.hold?.reason === reason ? r.hold : { reason, since: rfc3339(now) }
}

/**
 * The C4.2 state machine for one answered (or unanswered) attempt of an
 * op_key record. `done` needs the kind's done check (`isDone`).
 */
export function settleAttempt(
  r: OpRecord, x: HttpExchange, body: unknown, now: Date, u: number, isDone: (body: unknown) => boolean = isAddReceipt,
): Settled {
  const from: OpState = r.dispatched_from === OP_STATE.ambiguous ? OP_STATE.ambiguous : OP_STATE.pending
  let outcome = opOutcome(x)
  if (outcome === OUTCOME.done && !isDone(body)) outcome = OUTCOME.ambiguous
  const status = x.kind === 'answered' ? x.status : undefined
  const err = (kind: string): OpRecord['last_error'] => ({
    kind, ...(status !== undefined ? { status } : {}),
    ...(x.kind === 'answered' && x.detail ? { detail: x.detail } : {}),
  })
  const backoff = (n: number) => rfc3339(new Date(now.getTime() + backoffDelayMs(n, u, x.kind === 'answered' ? x.retryAfterS : null)))
  switch (outcome) {
    case OUTCOME.done:
      return { outcome, record: { ...r, state: OP_STATE.done, hold: null, receipt: body, last_error: null, next_attempt_at: null, backoff_n: 0, dispatched_from: null } }
    case OUTCOME.transient: {
      const n = (r.backoff_n ?? 0) + 1
      // A record that was ever ambiguous never returns to pending by a transient.
      return { outcome, record: { ...r, state: from, backoff_n: n, next_attempt_at: backoff(n), last_error: err(x.kind), dispatched_from: null } }
    }
    case OUTCOME.ambiguous: {
      const n = (r.backoff_n ?? 0) + 1
      return { outcome, record: { ...r, state: OP_STATE.ambiguous, backoff_n: n, next_attempt_at: backoff(n), last_error: err(x.kind), dispatched_from: null } }
    }
    case OUTCOME.needsSession:
      // Not an attempt: back to where it came from, held for the session.
      return { outcome, record: { ...r, state: from, attempts: Math.max(0, r.attempts - 1), hold: holdOn(r, HOLD.awaitingSession, now), last_error: err('needs_session'), dispatched_from: null } }
    case OUTCOME.heldSurfaceUnavailable:
      return { outcome, record: { ...r, state: from, hold: holdOn(r, HOLD.surfaceUnavailable, now), last_error: err(x.kind), dispatched_from: null } }
    case OUTCOME.heldClaimRequired:
      return { outcome, record: { ...r, state: from, hold: holdOn(r, HOLD.claimRequired, now), last_error: err(x.kind), dispatched_from: null } }
    case OUTCOME.heldNeedsReview:
      return { outcome, record: { ...r, state: from, hold: holdOn(r, HOLD.needsReview, now), last_error: err(x.kind), dispatched_from: null } }
    case OUTCOME.failedPermanent:
    default:
      return { outcome: OUTCOME.failedPermanent, record: { ...r, state: OP_STATE.failedPermanent, last_error: err(x.kind), next_attempt_at: null, dispatched_from: null } }
  }
}

/** recovered / session_restored: every waiting head is due now (n unchanged). */
export function makeDue(r: OpRecord): OpRecord {
  return r.next_attempt_at ? { ...r, next_attempt_at: null } : r
}

/** Never let an update touch the immutable part of a record. */
export function assertImmutable(before: OpRecord, after: OpRecord): void {
  for (const f of IMMUTABLE_FIELDS) {
    if (JSON.stringify(before[f]) !== JSON.stringify(after[f])) throw new Error(`immutable field changed: ${f}`)
  }
}

// ---- Storage ----------------------------------------------------------------------------------

export function readQueue(ownerKey: string): Promise<OpRecord[]> {
  return ownerGetAll<OpRecord>(ownerKey, STORE.queue)
}

/** Durable before anything is sent (C4 inv. 1). */
export function putRecords(ctx: WriterCtx, records: OpRecord[], guard?: () => boolean): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.queue], tx => {
    const s = tx.objectStore(STORE.queue)
    for (const r of records) s.put(r, r.op_id)
  }, guard)
}

/** Update one record's mutable part (the immutable part is checked against the stored one). */
export function updateRecord(ctx: WriterCtx, next: OpRecord): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.queue], tx => {
    const s = tx.objectStore(STORE.queue)
    const g = s.get(next.op_id)
    g.onsuccess = () => {
      const cur = g.result as OpRecord | undefined
      if (!cur) return
      assertImmutable(cur, next)
      s.put(next, next.op_id)
    }
  })
}

export function deleteRecords(ctx: WriterCtx, opIds: string[]): Promise<FencedResult> {
  return fencedWrite(ctx, [STORE.queue], tx => {
    const s = tx.objectStore(STORE.queue)
    for (const id of opIds) s.delete(id)
  })
}

/** At launch: in_flight -> ambiguous. */
export async function recoverQueueAtLaunch(ctx: WriterCtx): Promise<number> {
  const all = await readQueue(ctx.ownerKey)
  const moved = all.filter(r => r.state === OP_STATE.inFlight).map(recoverLaunchState)
  if (moved.length === 0) return 0
  return (await putRecords(ctx, moved)) === 'ok' ? moved.length : 0
}

/** C3 step 3 and the offline bar: records not done and not failed. */
export function isOpen(r: OpRecord): boolean {
  return r.state !== OP_STATE.done && r.state !== OP_STATE.failedPermanent
}

// ---- Review (C4.5) ------------------------------------------------------------------------------

export type ReviewOption = 'send' | 'discard' | 'add_again_as_new'

/** The user chose Send from review: allowed only below 29 days (decideHead re-checks the age). */
export function approveSend(r: OpRecord): OpRecord {
  return { ...r, review_send: true, hold: null }
}

export function reviewOptions(r: OpRecord, now: number): ReviewOption[] {
  const age = ageDays(r, now)
  if (age < SEND_ALLOWED_BELOW_DAYS) return ['send', 'discard']
  return r.kind === OP_KIND.collectionAdd ? ['discard', 'add_again_as_new'] : ['discard']
}

/** "Add again as new": a new group with a new base for the same items (the prompt warns when it was ambiguous). */
export function addAgainAsNew(r: OpRecord, ctx: WriterCtx, now: Date): OpRecord[] {
  return buildCollectionAddRecords({
    ownerKey: ctx.ownerKey, incarnation: ctx.incarnation, collectionId: r.scope.id, items: itemsOf(r), now,
  })
}

/** "Retry failed items": a NEW group with a new base for only the failed inputs. */
export function retryFailedAsNew(r: OpRecord, ctx: WriterCtx, now: Date): OpRecord[] {
  const receipt = r.receipt as AddReceipt | null
  if (!receipt || !isAddReceipt(receipt)) return []
  const items = itemsOf(r)
  const failed = receipt.errors.map(e => items[e.index]).filter((x): x is AddItemWire => !!x)
  if (failed.length === 0) return []
  return buildCollectionAddRecords({ ownerKey: ctx.ownerKey, incarnation: ctx.incarnation, collectionId: r.scope.id, items: failed, now })
}

// ---- The drain (collection adds) ----------------------------------------------------------------

export interface DrainDeps {
  ctx: WriterCtx
  /** Re-read after EVERY await (owner, incarnation, session subject, gate, scope). */
  current: () => DispatchCurrent
  /** Send the frozen bytes; never re-serialize. */
  send: (r: OpRecord) => Promise<{ exchange: HttpExchange; body: unknown }>
  now: () => Date
  u: () => number
  signal?: AbortSignal
  /** A settled attempt (receipts, holds, failures) for the UI. */
  onSettled?: (r: OpRecord, s: Settled, reconciliation: Reconciliation | null) => void
}

function ownedNow(deps: DrainDeps, r: OpRecord): boolean {
  if (deps.signal?.aborted) return false
  const d = decideHead({ ...r, state: OP_STATE.pending, next_attempt_at: null, hold: null }, deps.current(), deps.now().getTime())
  return d.decision === 'dispatch'
}

/**
 * Drain the collection_add records FIFO per scope, one request at a time.
 * An outcome whose write is refused (the fence moved meanwhile) is simply
 * not recorded: the record stays in_flight, becomes ambiguous at the next
 * launch and is settled by replaying its op_key (the server ledger answers
 * the stored response), so every write lands exactly once.
 */
export async function drainCollectionQueue(deps: DrainDeps): Promise<number> {
  let sent = 0
  const all = (await readQueue(deps.ctx.ownerKey)).filter(r => r.kind === OP_KIND.collectionAdd)
  for (const list of orderQueue(all).values()) {
    for (const head of list) {
      if (deps.signal?.aborted) return sent
      const now = deps.now()
      const d = decideHead(head, deps.current(), now.getTime())
      if (d.decision === 'skip') continue
      if (d.decision === 'hold') {
        if (head.hold?.reason !== d.reason) await updateRecord(deps.ctx, { ...head, hold: { reason: d.reason, since: rfc3339(now) } })
        break
      }
      if (d.decision !== 'dispatch' && d.decision !== 'replay') break
      const inflight = markInFlight(head, now)
      if ((await updateRecord(deps.ctx, inflight)) !== 'ok') return sent
      if (!ownedNow(deps, head)) {
        // Not sent: back where it was (if this write is refused too, launch
        // recovery makes it ambiguous and a replay settles it).
        await updateRecord(deps.ctx, { ...head, hold: { reason: HOLD.awaitingSession, since: rfc3339(now) } })
        return sent
      }
      const res = await deps.send(inflight)
      sent += 1
      const settled = settleAttempt(inflight, res.exchange, res.body, deps.now(), deps.u())
      if ((await updateRecord(deps.ctx, settled.record)) !== 'ok') return sent
      let rec: Reconciliation | null = null
      if (settled.record.state === OP_STATE.done && isAddReceipt(settled.record.receipt)) {
        rec = reconcileAddReceipt(settled.record, settled.record.receipt)
        // Reconciled: removed, unless some items failed (kept for the user to see and retry).
        if (Object.keys(rec.failed).length === 0) await deleteRecords(deps.ctx, [settled.record.op_id])
      }
      deps.onSettled?.(settled.record, settled, rec)
      if (settled.record.state === OP_STATE.done || settled.record.state === OP_STATE.failedPermanent) continue
      break
    }
  }
  return sent
}

/** recovered / session_restored: every waiting head is due now (n unchanged). */
export async function makeQueueDue(ctx: WriterCtx): Promise<void> {
  const q = (await readQueue(ctx.ownerKey)).filter(r => r.next_attempt_at || r.hold?.reason === HOLD.awaitingSession)
  if (q.length === 0) return
  await putRecords(ctx, q.map(r => ({ ...makeDue(r), hold: r.hold?.reason === HOLD.awaitingSession ? null : r.hold })))
}

export { DECISION }
